/**
 * /decks/:id — the editor view.
 *
 * Three-column layout: slide rail (left) · server-rendered preview iframe
 * (center) · inspector (right). PR1 ships read-only inspectors; PR2
 * replaces them with per-layout edit forms; PR3 adds slide management
 * (add / duplicate / delete / drag-reorder).
 *
 * Wiring philosophy: the rail and inspector remain dumb-ish components.
 * This shell owns the selection state, the layout-picker modal state,
 * and the slide-management mutations (insert / delete / reorder). It
 * passes handlers down so each child can stay focused on its rendering.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  useDeck,
  useDeleteSlide,
  useInsertSlide,
  useReorderSlides,
  useUpdateSlide,
} from "../hooks";
import { useInPlaceEdit, type InPlaceEdit } from "../inplace/useInPlaceEdit";
import { useBlockDnd, type BlockMutation } from "../inplace/useBlockDnd";
import { applyBlockMutation, editPathSplice } from "../inplace/editPathMap";
import { useSlideAutosave } from "../useSlideAutosave";
import type { Deck } from "../types";
import {
  newSlideFromTemplate,
  type SlideTemplate,
} from "../slideTemplates";
import { DeckEditorTopBar } from "./DeckEditorTopBar";
import { Inspector } from "./Inspector";
import { LayoutPickerModal } from "./LayoutPickerModal";
import { PreviewFrame } from "./PreviewFrame";
import { SlideRail } from "./SlideRail";

/**
 * Stable empty-deck placeholder so the hooks below (``useSlideAutosave``,
 * ``useInPlaceEdit``, ``useBlockDnd``) can be instantiated unconditionally
 * — React requires a stable hook order, but the deck query may still be
 * loading. The autosave never fires against this (its slide list is empty
 * and ``flush`` only runs from real edits once the deck has loaded).
 */
const EMPTY_DECK: Deck = {
  id: "",
  project_id: "",
  slug: "",
  title: "",
  theme: "",
  root_node_id: null,
  version: 0,
  deck_json: {
    schemaVersion: 1,
    docTitle: "",
    cacheTag: "",
    provenance: {},
    slides: [],
  },
  audience_class: null,
  audience_domains: null,
  created_by: null,
  created_at: "",
  updated_at: "",
};

export function DeckEditor() {
  const params = useParams<{ deckId: string }>();
  const deckId = params.deckId;
  const navigate = useNavigate();
  const { data: deck, isLoading, isError, error } = useDeck(deckId);
  const [activeIdx, setActiveIdx] = useState(0);
  const [layoutPickerOpen, setLayoutPickerOpen] = useState(false);
  // ADR-002 Phase C/D — direct-manipulation editing toggle.
  const [editMode, setEditMode] = useState(false);

  const slides = useMemo(() => deck?.deck_json.slides ?? [], [deck]);
  // Clamp activeIdx if the deck shrinks below the current selection.
  const clampedIdx = Math.min(activeIdx, Math.max(slides.length - 1, 0));

  // PR3 mutations — only meaningful once the deck has loaded but we
  // unconditionally instantiate so hook order stays stable across the
  // load/error early-return below.
  const insertSlide = useInsertSlide(deck?.id ?? "");
  const deleteSlide = useDeleteSlide(deck?.id ?? "");
  const reorderSlides = useReorderSlides(deck?.id ?? "");
  const updateSlide = useUpdateSlide(deck?.id ?? "");

  const slideManagementBusy =
    insertSlide.isPending ||
    deleteSlide.isPending ||
    reorderSlides.isPending;

  // ── ADR-002 Phase C/D: lifted autosave + in-place edit / DnD wiring ──────
  //
  // The slide autosave is LIFTED here (was in Inspector) so the Inspector's
  // plain-text fields and the preview's in-place edits write through the
  // *same* debounced writer — single writer per slide, no last-write-wins
  // races between the two panes (ADR-002 §4). Instantiated unconditionally
  // against ``EMPTY_DECK`` while the query loads to keep hook order stable.
  const { draft, setField, flush, saveState } = useSlideAutosave(
    deck ?? EMPTY_DECK,
    clampedIdx,
  );

  // The live preview iframe, captured via ``PreviewFrame``'s ``onReady`` on
  // every load (first mount + each cacheTag reload). The hooks reach into
  // its same-origin document to attach their delegated listeners.
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  // Bumped on every iframe ``load`` so the hooks' effects re-run and rebind
  // onto the *new* ``contentDocument`` after a cacheTag reload (ADR-002 §5
  // — the old document, and thus the old listeners, are discarded). Folding
  // it into ``getIframe``'s identity is the rebind seam the hooks expect.
  const [loadNonce, setLoadNonce] = useState(0);
  const getIframe = useCallback(
    () => iframeRef.current,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadNonce],
  );

  // Persist an in-place text edit against the slide where editing began.
  // The preview can navigate before blur/teardown commits the node, so never
  // assume the current rail selection still owns the edit-path.
  const handleInPlaceSave = useCallback(
    (edit: InPlaceEdit) => {
      if (edit.slideIdx === null) return;
      const sourceBody =
        edit.slideIdx === clampedIdx
          ? draft.bodyHtml
          : deck?.deck_json.slides[edit.slideIdx]?.bodyHtml;
      if (sourceBody === undefined) return;

      const next = editPathSplice(sourceBody, edit.editPath, edit.text);
      if (next === sourceBody) return;

      if (edit.slideIdx === clampedIdx) {
        setField("bodyHtml", next);
        flush();
        return;
      }

      updateSlide.mutate({
        slideIdx: edit.slideIdx,
        payload: { bodyHtml: next },
        ifMatchVersion: deck?.version,
      });
    },
    [clampedIdx, deck, draft.bodyHtml, setField, flush, updateSlide],
  );

  // Persist a block move/insert. Like in-place text edits, a drag in the plain
  // ``edit`` layout can land on ANY visible slide — not just the rail's active
  // one — so resolve the slide the mutation carries (its target's
  // ``[data-slide-idx]`` section), falling back to the active slide for the
  // single-slide layouts. Apply the splice to THAT slide's ``bodyHtml`` and
  // write it: through the lifted autosave writer when it's the active slide
  // (shared with the Inspector), or a direct slide PUT otherwise.
  const handleBlockMutate = useCallback(
    (mutation: BlockMutation) => {
      const slideIdx = mutation.slideIdx ?? clampedIdx;
      const sourceBody =
        slideIdx === clampedIdx
          ? draft.bodyHtml
          : deck?.deck_json.slides[slideIdx]?.bodyHtml;
      if (sourceBody === undefined) return;

      const next = applyBlockMutation(sourceBody, mutation);
      if (next === sourceBody) return;

      if (slideIdx === clampedIdx) {
        setField("bodyHtml", next);
        flush();
        return;
      }
      updateSlide.mutate({
        slideIdx,
        payload: { bodyHtml: next },
        ifMatchVersion: deck?.version,
      });
    },
    [clampedIdx, deck, draft.bodyHtml, setField, flush, updateSlide],
  );

  const inPlaceApi = useInPlaceEdit({
    getIframe,
    enabled: editMode,
    onSave: handleInPlaceSave,
  });
  const blockDnd = useBlockDnd({
    getIframe,
    enabled: editMode,
    onMutate: handleBlockMutate,
  });

  // ``useInPlaceEdit`` binds its own document-level listeners in an effect
  // (keyed on ``getIframe`` identity → rebinds on reload). ``useBlockDnd`` is
  // imperative, so drive its attach/detach from here on the same triggers:
  // edit mode flipping (``attach``/``detach`` identities change with
  // ``enabled``) and each iframe reload (``loadNonce`` → ``getIframe`` →
  // ``attach``). Detach on cleanup so a reload/disable tears the old
  // listeners off the discarded document.
  const { attach: attachDnd, detach: detachDnd } = blockDnd;
  useEffect(() => {
    attachDnd();
    return () => detachDnd();
  }, [attachDnd, detachDnd]);

  // ── Reload-vs-edit race gate (ADR-002 §4) ────────────────────────────────
  //
  // The preview reloads whenever its ``cacheTag`` prop changes — which is
  // every save. A save landing mid-type would yank the DOM out from under
  // an active ``contentEditable``. So we FREEZE the tag handed to
  // ``PreviewFrame`` while a node is being edited, releasing it to the
  // latest ``v{version}-{cacheTag}`` only once editing ends. We keep the
  // last-applied tag in a ref and only advance it when ``!isEditing()``.
  const liveTag = deck
    ? `v${deck.version}-${deck.deck_json.cacheTag}`
    : "";
  const frozenTagRef = useRef(liveTag);
  // Advance the tag when not actively editing, OR when it hasn't been seeded
  // with a real value yet (the deck loads before any edit can begin — you
  // can't be mid-edit on a DOM that was never rendered, so there's nothing
  // to protect from a reload in that window).
  if (!inPlaceApi.isEditing() || frozenTagRef.current === "") {
    frozenTagRef.current = liveTag;
  }
  const previewCacheTag = frozenTagRef.current;

  if (isLoading) {
    return (
      <ScreenShell>
        <p style={{ padding: "2rem", color: "var(--gray-9)" }}>Loading deck…</p>
      </ScreenShell>
    );
  }
  if (isError || !deck) {
    return (
      <ScreenShell>
        <ErrorBox
          title="Could not load this deck"
          message={error instanceof Error ? error.message : "Unknown error"}
          onBack={() => navigate("/decks")}
        />
      </ScreenShell>
    );
  }

  // Capture once after the narrowing guard above so TypeScript stops
  // worrying about deck being undefined inside the handlers below.
  const loadedDeck = deck;

  function handlePickLayout(template: SlideTemplate) {
    setLayoutPickerOpen(false);
    // Insert *after* the currently-selected slide so the new slide lands
    // where the user is likely paying attention. If nothing is selected
    // (e.g. an empty deck somehow), append.
    const insertAt = slides.length === 0 ? 0 : clampedIdx + 1;
    insertSlide.mutate(
      {
        payload: {
          at: insertAt,
          slide: newSlideFromTemplate(template),
        },
        ifMatchVersion: loadedDeck.version,
      },
      {
        onSuccess: ({ newIdx }) => {
          setActiveIdx(newIdx);
          // Scroll the rail to the new row on the next paint so the
          // freshly-rendered DOM has the right children to query.
          requestAnimationFrame(() => {
            const row = document.querySelector(
              `[data-testid='slide-rail-row-${newIdx}']`,
            );
            // Optional-chain the method call too: jsdom doesn't implement
            // scrollIntoView, so without the guard the rAF callback throws
            // an unhandled exception after the vitest test resolves and
            // fails the deploy's `Run tests` step. Real browsers always
            // have the method; this is purely a test-environment guard.
            row?.scrollIntoView?.({ block: "nearest" });
          });
        },
      },
    );
  }

  function handleDuplicateSlide(idx: number) {
    const source = slides[idx];
    if (!source) return;
    insertSlide.mutate(
      {
        payload: {
          at: idx + 1,
          slide: {
            section: source.section,
            slug: source.slug,
            titleHtml: source.titleHtml,
            meta: source.meta,
            bodyHtml: source.bodyHtml,
            renderPrefix: source.renderPrefix,
            renderSignature: source.renderSignature,
          },
        },
        ifMatchVersion: loadedDeck.version,
      },
      {
        onSuccess: ({ newIdx }) => setActiveIdx(newIdx),
      },
    );
  }

  function handleDeleteSlide(idx: number) {
    // Picked the previous slide as the new selection — or the first slide
    // if we just deleted the very first. Falls back to 0 if the deck ends
    // up empty (the route refuses this, but the clamp avoids a negative).
    const nextSelection = Math.max(0, idx - 1);
    deleteSlide.mutate(
      { slideIdx: idx, ifMatchVersion: loadedDeck.version },
      {
        onSuccess: () => setActiveIdx(nextSelection),
      },
    );
  }

  function handleReorder(newOrder: number[]) {
    // Track which original-idx is the currently-selected slide so we can
    // keep the same slide selected after the reorder lands.
    const selectedOriginalIdx = clampedIdx;
    const newSelectionIdx = newOrder.indexOf(selectedOriginalIdx);
    reorderSlides.mutate(
      { payload: { order: newOrder }, ifMatchVersion: loadedDeck.version },
      {
        onSuccess: () => {
          if (newSelectionIdx >= 0) setActiveIdx(newSelectionIdx);
        },
      },
    );
  }

  return (
    <ScreenShell>
      <DeckEditorTopBar deck={deck} />
      <div style={threePaneStyle}>
        <SlideRail
          slides={slides}
          activeIdx={clampedIdx}
          onSelect={(idx) => setActiveIdx(idx)}
          onAdd={() => setLayoutPickerOpen(true)}
          onReorder={handleReorder}
          disabled={slideManagementBusy}
        />
        <div style={previewColumnStyle}>
          <div style={editModeBarStyle}>
            <span style={editModeHintStyle}>
              {editMode
                ? "Edit mode — plain layout. Click any text to edit; drag blocks to reorder."
                : "Present mode — 3D view."}
            </span>
            <button
              type="button"
              onClick={() => setEditMode((on) => !on)}
              aria-pressed={editMode}
              data-testid="edit-mode-toggle"
              title={
                editMode
                  ? "Switch to the 3D present view"
                  : "Switch to the plain edit layout"
              }
              style={editModeButtonStyle(editMode)}
            >
              {editMode ? "▶ Present" : "✎ Edit"}
            </button>
          </div>
          <PreviewFrame
            deckId={deck.id}
            // While a node is being edited in place we FREEZE this tag so a
            // mid-type autosave doesn't reload the iframe and discard the
            // editable node (ADR-002 §4). It releases to the live
            // ``v{version}-{cacheTag}`` once editing ends.
            cacheTag={previewCacheTag}
            // ADR-002 Phase A: selecting/reordering a slide in the rail
            // drives the preview to that slide.
            activeIdx={clampedIdx}
            // Phase C/D: surface edit mode + hand the iframe to the parent
            // so the in-place hooks can bind onto its document. Bump the
            // load nonce so the hooks rebind onto the freshly-loaded doc.
            editMode={editMode}
            onReady={(iframe) => {
              iframeRef.current = iframe;
              setLoadNonce((n) => n + 1);
            }}
          />
        </div>
        <Inspector
          deck={deck}
          activeIdx={clampedIdx}
          draft={draft}
          setField={setField}
          flush={flush}
          saveState={saveState}
          onDuplicateSlide={handleDuplicateSlide}
          onDeleteSlide={handleDeleteSlide}
          slideManagementBusy={slideManagementBusy}
        />
      </div>
      <LayoutPickerModal
        open={layoutPickerOpen}
        onPick={handlePickLayout}
        onClose={() => setLayoutPickerOpen(false)}
      />
    </ScreenShell>
  );
}

function ScreenShell({ children }: { children: React.ReactNode }) {
  return (
    <div style={shellStyle} data-testid="deck-editor-screen">
      {children}
    </div>
  );
}

function ErrorBox({
  title,
  message,
  onBack,
}: {
  title: string;
  message: string;
  onBack: () => void;
}) {
  return (
    <div
      style={{
        margin: "10vh auto",
        maxWidth: 480,
        textAlign: "center",
        color: "var(--gray-12)",
      }}
      data-testid="deck-editor-error"
    >
      <h2>{title}</h2>
      <p style={{ color: "var(--gray-10)" }}>{message}</p>
      <p>
        <button
          type="button"
          onClick={onBack}
          style={{
            background: "var(--gray-4)",
            border: "1px solid var(--gray-7)",
            padding: "0.4rem 0.9rem",
            borderRadius: 4,
            cursor: "pointer",
          }}
        >
          Back to deck list
        </button>
      </p>
      <p style={{ marginTop: "1rem", fontSize: "0.85em" }}>
        <Link to="/decks">/decks</Link>
      </p>
    </div>
  );
}

const shellStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  background: "var(--gray-1)",
  color: "var(--gray-12)",
};

const threePaneStyle: React.CSSProperties = {
  flex: 1,
  display: "flex",
  minHeight: 0,
};

const previewColumnStyle: React.CSSProperties = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
};

const editModeBarStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.75rem",
  padding: "0.4rem 0.75rem",
  borderBottom: "1px solid var(--gray-5)",
  background: "var(--gray-1)",
};

const editModeHintStyle: React.CSSProperties = {
  color: "var(--gray-9)",
  fontSize: "0.75em",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

function editModeButtonStyle(active: boolean): React.CSSProperties {
  return {
    padding: "0.3rem 0.8rem",
    borderRadius: 4,
    border: `1px solid ${active ? "var(--accent-8)" : "var(--gray-7)"}`,
    background: active ? "var(--accent-9)" : "var(--gray-3)",
    color: active ? "var(--accent-contrast, white)" : "var(--gray-12)",
    fontSize: "0.8em",
    fontWeight: 600,
    cursor: "pointer",
  };
}
